const WS_URL = 'wss://log.vidal-app.com/ws';

class WebSocketTaskService {
  constructor() {
    this.ws = null;
    this.tasks = [];
    this.listeners = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.STORAGE_KEY = 'ravena_tasks_state';
    
    // Load persisted tasks
    this.loadTasks();
  }

  loadTasks() {
    // FORCE CLEAR FOR DEBUG (Requested by user to clear stuck tasks)
    if (!localStorage.getItem('vidal_tasks_fixed_v1')) {
        localStorage.removeItem(this.STORAGE_KEY);
        localStorage.setItem('vidal_tasks_fixed_v1', 'true');
        this.tasks = [];
        this.saveTasks();
        return;
    }

    // Clear stale tasks (v4 cleanup - forceful)
    // We bump version to ensure everyone gets cleared
    if (!localStorage.getItem('vidal_tasks_fixed_v4')) {
        this.tasks = [];
        this.saveTasks();
        localStorage.setItem('vidal_tasks_fixed_v4', 'true');
        return;
    }

    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      this.tasks = JSON.parse(stored);
      // Remove tasks older than 24h OR tasks with undefined name/message
      const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
      this.tasks = this.tasks.filter(t => {
          const isRecent = new Date(t.timestamp).getTime() > oneDayAgo;
          const isValid = t.message && t.message !== 'undefined';
          const isNotGhost = t.name !== 'undefined'; 
          return isRecent && isValid && isNotGhost;
      });
      this.saveTasks();
    }
  }

  saveTasks() {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.tasks));
    this.notifyListeners();
  }

  connect() {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    // Get Auth Token for connection
    const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
    const token = authData.token;
    
    // We append token if needed by server, or send in first message
    // Assuming query param for now based on typical setup, verify if headers supported
    const urlWithAuth = token ? `${WS_URL}?token=${token}` : WS_URL;

    this.ws = new WebSocket(urlWithAuth);

    this.ws.onopen = () => {
      console.log('Task WebSocket Connected');
      this.reconnectAttempts = 0;
      
      // Subscribe to admin task channels for archive/restore operations
      // Using projectId "admin" to match worker messages
      if (this.ws.readyState === WebSocket.OPEN) {
        // Subscribe to archive_index channel
        this.ws.send(JSON.stringify({ 
          action: 'subscribe', 
          type: 'archive_index',
          projectId: 'admin'
        }));
        
        // Subscribe to restore_index channel
        this.ws.send(JSON.stringify({ 
          action: 'subscribe', 
          type: 'restore_index',
          projectId: 'admin'
        }));
        
        // Subscribe to process_speds_revisor and download_sped channels
        const currentProject = localStorage.getItem('vidal_selected_project');
        if (currentProject) {
          this.ws.send(JSON.stringify({ 
            action: 'subscribe', 
            type: 'process_speds_revisor',
            projectId: currentProject
          }));
          
          this.ws.send(JSON.stringify({ 
            action: 'subscribe', 
            type: 'download_sped',
            projectId: currentProject
          }));
        }
        
        console.log('Task WebSocket Subscribed to archive_index, restore_index, process_speds_revisor, and download_sped channels');
      }
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Parse incoming message from WebSocket server
        // Expected: { success: true, message: { type: 'X', projectId: 'Y', data: {...} } }
        // OR directly from broadcast: { success: true, message: { type: 'archive_index', ... } }
        if (data.success && data.message) {
          const { type, data: taskData } = data.message;
          
          // Accept task-related message types
          const TASK_TYPES = ['tasks', 'archive_index', 'restore_index', 'process_nfes', 'process_sped', 'process_speds_revisor'];
          
          if (TASK_TYPES.includes(type)) {
            // Normalize the update object for handleTaskUpdate
            const update = {
              operationId: data.message.operationId || taskData?.operationId,
              status: data.message.status || taskData?.status,
              progress: data.message.progress ?? taskData?.progress,
              message: data.message.message || taskData?.message || taskData?.stage,
              indexName: data.message.indexName || taskData?.indexName,
              timestamp: data.message.timestamp || new Date().toISOString(),
              task: type, // Use the type as the task type
              tasks: data.message.tasks || taskData?.tasks // Preserve detailed tasks array
            };
            
            if (update.operationId) {
              console.log('Task WebSocket Update received:', update);
              this.handleTaskUpdate(update);
            }
          }
        }
      } catch (e) {
        console.error('Error parsing WS message:', e);
      }
    };

    this.ws.onclose = () => {
      console.log('Task WebSocket Disconnected');
      this.attemptReconnect();
    };

    this.ws.onerror = (error) => {
      console.error('Task WebSocket Error:', error);
    };
  }

  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      setTimeout(() => {
        this.reconnectAttempts++;
        this.connect();
      }, 3000 * this.reconnectAttempts);
    }
  }

  handleTaskUpdate(update) {
    const { operationId, status, progress, message, indexName, timestamp, tasks } = update;
    
    const existingIndex = this.tasks.findIndex(t => t.operationId === operationId);
    
    const taskData = {
      operationId,
      indexName,
      status, 
      progress,
      message,
      tasks, // Store tasks array
      timestamp: timestamp || new Date().toISOString(),
      // Infer type if new
      type: update.task || 'unknown_task'
    };
    
    if (status === 'completed') {
      taskData.completedAt = new Date().toISOString();
    } else if (existingIndex === -1) {
      taskData.startedAt = new Date().toISOString();
    } else {
        // Keep original startedAt
        taskData.startedAt = this.tasks[existingIndex].startedAt;
    }

    if (existingIndex > -1) {
      this.tasks[existingIndex] = { ...this.tasks[existingIndex], ...taskData };
    } else {
      this.tasks.push(taskData);
    }

    this.saveTasks();
  }

  subscribe(listener) {
    this.listeners.push(listener);
    listener(this.tasks); // Initial callback
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener(this.tasks));
  }
  
  // Method to manually add a task (when user clicks button and gets 202 response)
  addTask(task) {
    const existingIndex = this.tasks.findIndex(t => t.operationId === task.operationId);
    if (existingIndex === -1) {
        this.tasks.push({
            ...task,
            status: 'running',
            progress: 0,
            startedAt: new Date().toISOString(),
            timestamp: new Date().toISOString()
        });
        this.saveTasks();
    }
  }

  removeTask(operationId) {
    this.tasks = this.tasks.filter(t => t.operationId !== operationId);
    this.saveTasks();
  }
  
  clearAllTasks() {
    this.tasks = [];
    this.saveTasks();
  }
  
  getTasks() {
    return this.tasks;
  }

  updateProjectSubscription(projectId) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
       console.log(`[WebSocketTaskService] Updating subscription for project ${projectId}`);
       this.ws.send(JSON.stringify({ 
        action: 'subscribe', 
        type: 'process_speds_revisor',
        projectId: projectId
      }));
    }
  }
}

export default new WebSocketTaskService();
